import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Palette, Upload, Trash2, Headphones, Save } from "lucide-react";

const PALETTE = ["#059669", "#0EA5E9", "#2563EB", "#7C3AED", "#DB2777", "#DC2626", "#EA580C", "#CA8A04", "#0F766E", "#334155"];

export default function Branding() {
  const { branding, set } = useBranding();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/branding").then((r) => setForm(r.data)); }, []);
  if (!form) return <div className="p-8 text-slate-500">Carregando...</div>;

  const save = async () => {
    if (!form.system_name.trim()) return toast.error("Informe o nome do sistema");
    setSaving(true);
    try {
      const r = await api.put("/branding", { system_name: form.system_name, company_name: form.company_name, primary_color: form.primary_color, font: form.font });
      set(r.data); setForm(r.data); toast.success("Personalização salva");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Envie uma imagem (PNG, JPG, SVG ou WEBP)");
    if (file.size > 2 * 1024 * 1024) return toast.error("Imagem muito grande (máx. 2 MB)");
    const data = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
    try { const r = await api.post("/branding/logo", { data, mimetype: file.type, filename: file.name }); set(r.data); setForm(r.data); toast.success("Logotipo atualizado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const removeLogo = async () => {
    if (!window.confirm("Remover o logotipo e voltar ao ícone padrão?")) return;
    try { const r = await api.delete("/branding/logo"); set(r.data); setForm(r.data); toast.success("Logotipo removido"); } catch (e) { toast.error(errMsg(e)); }
  };

  const logoSrc = branding.logo_url;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Personalização" subtitle="Configuração global do sistema (somente Super Admin): nome do sistema, empresa, logotipo, cor principal e fonte. Vale para todas as empresas e usuários.">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-branding-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Salvando..." : "Salvar alterações"}</Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 sm:p-6 border-slate-200 dark:border-slate-800 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Nome do sistema *</Label><Input data-testid="brand-system-name" value={form.system_name} onChange={(e) => setForm({ ...form, system_name: e.target.value })} placeholder="OS Flow" /></div>
            <div><Label>Nome da empresa</Label><Input data-testid="brand-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Nome da sua empresa (provedora do sistema)" /></div>
          </div>

          <div>
            <Label className="flex items-center gap-1"><Palette className="w-3.5 h-3.5" /> Cor principal</Label>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              {PALETTE.map((c) => <button key={c} onClick={() => setForm({ ...form, primary_color: c })} className={`w-8 h-8 rounded-full border-2 transition-transform ${form.primary_color.toLowerCase() === c.toLowerCase() ? "border-slate-900 dark:border-white scale-110" : "border-transparent"}`} style={{ background: c }} data-testid={`brand-color-${c.slice(1)}`} aria-label={c} />)}
              <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer" data-testid="brand-color-input" />
              <span className="font-mono text-xs text-slate-500">{form.primary_color}</span>
            </div>
          </div>

          <div className="sm:w-1/2"><Label>Fonte</Label>
            <Select value={form.font} onValueChange={(v) => setForm({ ...form, font: v })}>
              <SelectTrigger data-testid="brand-font-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-900">{(form.fonts || []).map((f) => <SelectItem key={f} value={f}><span style={{ fontFamily: f }}>{f}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Logotipo</Label>
            <div className="mt-2 flex items-center gap-4 flex-wrap">
              <div className="w-20 h-20 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 flex items-center justify-center overflow-hidden" data-testid="brand-logo-preview">
                {logoSrc ? <img src={logoSrc} alt="Logotipo" className="w-full h-full object-contain" /> : <Headphones className="w-8 h-8 text-slate-400" />}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="brand-logo-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="brand-logo-upload-btn"><Upload className="w-4 h-4 mr-2" /> Enviar imagem</Button>
              {logoSrc && <Button variant="outline" className="text-rose-600" onClick={removeLogo} data-testid="brand-logo-remove-btn"><Trash2 className="w-4 h-4 mr-2" /> Remover</Button>}
              <p className="text-xs text-slate-500 w-full">PNG, JPG, SVG ou WEBP até 2 MB. Recomendado: fundo transparente, formato quadrado.</p>
            </div>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 dark:border-slate-800 space-y-4" data-testid="brand-preview">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pré-visualização</p>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 flex items-center gap-3" style={{ fontFamily: `"${form.font}", sans-serif` }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden shrink-0" style={{ background: logoSrc ? "transparent" : form.primary_color }}>
              {logoSrc ? <img src={logoSrc} alt="" className="w-full h-full object-contain" /> : <Headphones className="w-5 h-5 text-white" />}
            </div>
            <div className="min-w-0"><p className="font-extrabold text-slate-900 dark:text-white truncate">{form.system_name || "OS Flow"}</p><p className="text-xs text-slate-500 truncate">{form.company_name || "Empresa"}</p></div>
          </div>
          <div className="space-y-2" style={{ fontFamily: `"${form.font}", sans-serif` }}>
            <button className="w-full h-10 rounded-md text-white font-semibold" style={{ background: form.primary_color }}>Botão principal</button>
            <div className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: `${form.primary_color}1A`, color: form.primary_color }}>Item de menu ativo</div>
            <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white">Título com a fonte</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Texto de exemplo com a fonte selecionada para você conferir a leitura.</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
